import { AppError } from '@imagise/entities';
import { EditMode } from './enums/editor.enum';
import {
  EditorAnimationCommittedEdit,
  EditorAnimationFrame,
  EditorAnimationParams,
  EditorCanvasViewState,
  EditorCommittedEdit,
  EditorFormValue,
  cloneAnimationFrame,
  cloneCommittedEdits,
  cloneDraftOperation,
  getAppliedCommittedEdits,
} from './editor-domain.models';
import {
  EditImageRequest,
  UnsupportedEditorEdit,
  buildEditImageRequestFromState,
} from './editor-request.adapter';

const PREVIEW_OUTPUT_FORMAT = 'png';
const ANIMATION_PREVIEW_OUTPUT_FORMAT = 'gif';

/**
 * Backend preview always renders the committed edit stack only. If live
 * backend preview of a draft (e.g. deskew) is ever needed, reintroduce a
 * 'committed-and-draft' scope here and feed the draft into the plan below.
 */
export type EditorBackendPreviewScope = 'committed';

export type EditorBackendPreviewPlan =
  | {
      status: 'idle';
      scope: null;
      request: null;
      requestKey: null;
      unsupportedEdits: [];
      error: null;
    }
  | {
      status: 'request';
      scope: EditorBackendPreviewScope;
      request: EditImageRequest;
      requestKey: string;
      unsupportedEdits: [];
      error: null;
    }
  | {
      status: 'unsupported';
      scope: EditorBackendPreviewScope;
      request: EditImageRequest | null;
      requestKey: string | null;
      unsupportedEdits: UnsupportedEditorEdit[];
      error: AppError;
    };

export interface EditorBackendPreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  scope: EditorBackendPreviewScope | null;
  objectUrl: string | null;
  error: AppError | null;
  requestKey: string | null;
}

export interface ResolvedEditorCanvasPreviewState {
  imageUrl: string | null;
  viewState: EditorCanvasViewState;
  loading: boolean;
  error: AppError | null;
}

export interface EditorAnimationCanvasPreview {
  frames: EditorAnimationFrame[];
  viewState: EditorCanvasViewState;
}

export const EMPTY_EDITOR_CANVAS_VIEW_STATE: EditorCanvasViewState = {
  previewUrl: null,
  committedEdits: [],
  draft: null,
};

export function createInitialEditorBackendPreviewState(): EditorBackendPreviewState {
  return {
    status: 'idle',
    scope: null,
    objectUrl: null,
    error: null,
    requestKey: null,
  };
}

export function buildBackendPreviewPlan(
  value: EditorFormValue,
): EditorBackendPreviewPlan {
  const committedEdits = getAppliedCommittedEdits(value);
  const lastCommittedBackendPreviewEditIndex =
    getLastCommittedBackendPreviewEditIndex(committedEdits);

  if (lastCommittedBackendPreviewEditIndex < 0) {
    return {
      status: 'idle',
      scope: null,
      request: null,
      requestKey: null,
      unsupportedEdits: [],
      error: null,
    };
  }

  const scope: EditorBackendPreviewScope = 'committed';
  const previewValue = createPreviewValue(
    value,
    committedEdits.slice(0, lastCommittedBackendPreviewEditIndex + 1),
  );
  const { request, unsupportedEdits } = buildEditImageRequestFromState(previewValue, {
    toFormat: getPreviewOutputFormat(previewValue),
  });

  if (unsupportedEdits.length > 0) {
    return {
      status: 'unsupported',
      scope,
      request: request.edits?.length ? request : null,
      requestKey: request.edits?.length ? createRequestKey(request, scope) : null,
      unsupportedEdits,
      error: createUnsupportedPreviewError(unsupportedEdits),
    };
  }

  return {
    status: 'request',
    scope,
    request,
    requestKey: createRequestKey(request, scope),
    unsupportedEdits: [],
    error: null,
  };
}

export function resolveEditorCanvasPreviewState(
  sourceImageUrl: string | null,
  rawViewState: EditorCanvasViewState,
  previewState: EditorBackendPreviewState,
): ResolvedEditorCanvasPreviewState {
  if (previewState.status === 'ready' && previewState.objectUrl) {
    return {
      imageUrl: previewState.objectUrl,
      viewState: {
        previewUrl: previewState.objectUrl,
        committedEdits: cloneCommittedEdits(
          getCommittedEditsAfterLastBackendPreviewEdit(rawViewState.committedEdits),
        ),
        draft: cloneDraftOperation(rawViewState.draft),
      },
      loading: false,
      error: null,
    };
  }

  if (previewState.status === 'loading' && previewState.objectUrl) {
    return {
      imageUrl: previewState.objectUrl,
      viewState: {
        previewUrl: previewState.objectUrl,
        committedEdits: [],
        draft: null,
      },
      loading: true,
      error: null,
    };
  }

  if (previewState.scope) {
    return {
      imageUrl: sourceImageUrl,
      viewState: {
        previewUrl: sourceImageUrl,
        committedEdits: cloneCommittedEdits(
          getCommittedEditsBeforeFirstBackendPreviewEdit(rawViewState.committedEdits),
        ),
        draft: null,
      },
      loading: previewState.status === 'loading',
      error: previewState.status === 'error' ? previewState.error : null,
    };
  }

  return {
    imageUrl: sourceImageUrl,
    viewState: {
      previewUrl: sourceImageUrl,
      committedEdits: cloneCommittedEdits(rawViewState.committedEdits),
      draft: cloneDraftOperation(rawViewState.draft),
    },
    loading: false,
    error: null,
  };
}

/**
 * Frames of the animation being previewed on the canvas: the live draft wins,
 * otherwise the latest applied animation edit — so the preview keeps playing
 * after Apply and in every other tool. The frames replace the source image,
 * so the canvas view state keeps only the edits stacked AFTER the animation
 * (plus the current draft, e.g. a crop rect or a filter preview on top of the
 * running animation); edits before it belong to the source image, not to the
 * frames.
 *
 * The view state opens with a synthetic frames-free animation edit: the
 * renderer turns it into the same aspect-fit-into-width×height box the
 * backend applies to every frame, so frames of different sizes don't resize
 * the canvas image (and the crop overlay) on every tick.
 */
export function resolveEditorAnimationCanvasPreview(
  value: EditorFormValue,
): EditorAnimationCanvasPreview | null {
  const draft = value.draft;
  if (draft?.kind === 'animation' && draft.params.frames.length > 1) {
    return {
      frames: draft.params.frames.map(frame => cloneAnimationFrame(frame)),
      viewState: {
        previewUrl: draft.params.frames[0].previewUrl,
        committedEdits: toAnimationFrameBoxEdits(draft.params),
        draft: null,
      },
    };
  }

  const appliedEdits = value.committedEdits.slice(0, value.history.cursor);
  for (let index = appliedEdits.length - 1; index >= 0; index -= 1) {
    const edit = appliedEdits[index];
    if (edit.kind === 'animation' && edit.params.frames.length > 1) {
      return {
        frames: edit.params.frames.map(frame => cloneAnimationFrame(frame)),
        viewState: {
          previewUrl: edit.params.frames[0].previewUrl,
          committedEdits: [
            ...toAnimationFrameBoxEdits(edit.params),
            ...cloneCommittedEdits(appliedEdits.slice(index + 1)),
          ],
          draft: cloneDraftOperation(value.draft),
        },
      };
    }
  }

  return null;
}

/**
 * The frame box is skipped while the animation size is still the 1×1
 * placeholder (the source dimensions have not arrived yet) — mirroring the
 * backend, which only resizes frames when both dimensions are set.
 */
function toAnimationFrameBoxEdits(
  params: EditorAnimationParams,
): EditorAnimationCommittedEdit[] {
  if (params.width <= 0 || params.height <= 0 || (params.width <= 1 && params.height <= 1)) {
    return [];
  }

  return [
    {
      id: 'animation-frame-box',
      kind: 'animation',
      mode: EditMode.Animation,
      params: {
        width: params.width,
        height: params.height,
        backgroundColor: params.backgroundColor,
        loopCount: params.loopCount,
        frames: [],
      },
    },
  ];
}

export function createPreviewRequestError(error: unknown): AppError {
  const wrappedError = error as { error?: AppError | { Error?: AppError } } | undefined;
  const nestedError = wrappedError?.error as AppError & { Error?: AppError } | undefined;
  const appError = nestedError?.Error ?? nestedError;

  if (appError?.ExceptionMessage) {
    return appError;
  }

  return {
    ExceptionMessage: 'Backend preview request failed.',
    Type: 'EditorBackendPreviewError',
    Details: 'Backend preview request failed.',
  };
}

function isBackendPreviewEdit(edit: EditorCommittedEdit): boolean {
  // Animation is previewed client-side (frame player on the canvas) — asking
  // the backend to assemble a GIF for preview duplicated the download request
  // and produced a static SVG <image> anyway.
  return edit.kind === 'deskew';
}

function getPreviewOutputFormat(value: EditorFormValue): string {
  const edits = value.committedEdits.slice(0, value.history.cursor);

  return edits.some(edit => edit.kind === 'animation')
    ? ANIMATION_PREVIEW_OUTPUT_FORMAT
    : PREVIEW_OUTPUT_FORMAT;
}

function getCommittedEditsBeforeFirstBackendPreviewEdit(
  committedEdits: readonly EditorCommittedEdit[],
): EditorCommittedEdit[] {
  const firstBackendPreviewEditIndex = committedEdits.findIndex(isBackendPreviewEdit);

  if (firstBackendPreviewEditIndex < 0) {
    return [...committedEdits];
  }

  return committedEdits.slice(0, firstBackendPreviewEditIndex);
}

function getCommittedEditsAfterLastBackendPreviewEdit(
  committedEdits: readonly EditorCommittedEdit[],
): EditorCommittedEdit[] {
  const lastBackendPreviewEditIndex =
    getLastCommittedBackendPreviewEditIndex(committedEdits);

  if (lastBackendPreviewEditIndex < 0) {
    return [...committedEdits];
  }

  return committedEdits.slice(lastBackendPreviewEditIndex + 1);
}

function getLastCommittedBackendPreviewEditIndex(
  committedEdits: readonly EditorCommittedEdit[],
): number {
  for (let index = committedEdits.length - 1; index >= 0; index -= 1) {
    if (isBackendPreviewEdit(committedEdits[index])) {
      return index;
    }
  }

  return -1;
}

function createPreviewValue(
  value: EditorFormValue,
  committedEdits: readonly EditorCommittedEdit[],
): EditorFormValue {
  return {
    ...value,
    committedEdits: cloneCommittedEdits(committedEdits),
    history: {
      cursor: committedEdits.length,
    },
  };
}

function createRequestKey(
  request: EditImageRequest,
  scope: EditorBackendPreviewScope,
): string {
  return JSON.stringify({
    scope,
    request,
  });
}

function createUnsupportedPreviewError(
  unsupportedEdits: UnsupportedEditorEdit[],
): AppError {
  const details = Array.from(
    new Set(unsupportedEdits.map(edit => edit.reason)),
  ).join(' ');

  return {
    ExceptionMessage: 'Backend preview is unavailable for the current edit stack.',
    Type: 'EditorBackendPreviewUnsupported',
    Details: details || 'Backend preview request contains unsupported edits.',
  };
}
